<script>
  import Layout from '/components/Layout.svelte';
  import { inertia } from '@inertiajs/svelte';

  export let counts = {};
  export let diagnostics = {};

  const groups = [
    { label: 'World',          items: ['locations', 'sublocations', 'buildings', 'shops'] },
    { label: 'Characters',     items: ['species', 'person_classes', 'attributes', 'skills'] },
    { label: 'Jobs & Effects', items: ['jobs', 'effects', 'items', 'traits'] },
    { label: 'Misc',           items: ['songs', 'name_lists'] },
  ];

  const eventItems = ['events', 'crew_events', 'quest_chains'];

  function typeLabel(t) {
    return t.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  }

  $: total = Object.values(counts).reduce((a, b) => a + b, 0);

  $: invalidSourceCount = diagnostics.invalid_event_references?.source_count ?? 0;
  $: invalidRefCount = diagnostics.invalid_event_references?.ref_count ?? 0;
  $: invalidAssetSourceCount = diagnostics.invalid_asset_references?.source_count ?? 0;
  $: invalidAssetRefCount = diagnostics.invalid_asset_references?.ref_count ?? 0;
  $: invalidYamlCount = diagnostics.invalid_yaml_syntax?.file_count ?? 0;
  $: manualEventCount = diagnostics.event_views?.manual_count ?? 0;
  $: orphanEventCount = diagnostics.event_views?.orphan_count ?? 0;
</script>

<svelte:head>
  <title>Dashboard — Age of Trevor Webedit</title>
</svelte:head>

<Layout title="Dashboard">
  <p class="summary">{total} YAML files across {Object.keys(counts).length} data types</p>

  {#each groups as group}
    <section class="group">
      <h2 class="group-label">{group.label}</h2>
      <div class="type-grid">
        {#each group.items as item}
          <a use:inertia href="/editor/{item}" class="type-card">
            <span class="type-name">{typeLabel(item)}</span>
            <span class="type-count">{counts[item] ?? 0} files</span>
          </a>
        {/each}
      </div>
    </section>
  {/each}

  <section class="group">
    <h2 class="group-label">Events</h2>
    <div class="type-grid">
      {#each eventItems as item}
        <a use:inertia href="/editor/{item}" class="type-card">
          <span class="type-name">{typeLabel(item)}</span>
          <span class="type-count">{counts[item] ?? 0} files</span>
        </a>
      {/each}
      <a use:inertia href="/event_views/manual" class="type-card view-card">
        <span class="type-name">Manual</span>
        <span class="type-count">{manualEventCount} events</span>
      </a>
      <a use:inertia href="/event_views/orphans" class="type-card view-card {orphanEventCount > 0 ? 'view-card-warn' : ''}">
        <span class="type-name">Orphans</span>
        <span class="type-count">{orphanEventCount} events</span>
      </a>
    </div>
  </section>

  <section class="group diagnostics-section">
    <h2 class="group-label">Diagnostics</h2>

    <div class="diag-grid">
      <a use:inertia href="/diagnostics/invalid_event_references"
         class="diag-card {invalidSourceCount === 0 ? 'ok' : 'warn'}">
        <span class="diag-title">Invalid event references</span>
        <span class="diag-badge {invalidSourceCount === 0 ? 'badge-ok' : 'badge-warn'}">
          {invalidSourceCount === 0 ? 'All clear' : `${invalidSourceCount} events, ${invalidRefCount} refs`}
        </span>
      </a>

      <a use:inertia href="/diagnostics/invalid_asset_references"
         class="diag-card {invalidAssetSourceCount === 0 ? 'ok' : 'warn'}">
        <span class="diag-title">Invalid asset references</span>
        <span class="diag-badge {invalidAssetSourceCount === 0 ? 'badge-ok' : 'badge-warn'}">
          {invalidAssetSourceCount === 0 ? 'All clear' : `${invalidAssetSourceCount} events, ${invalidAssetRefCount} refs`}
        </span>
      </a>

      <a use:inertia href="/diagnostics/invalid_yaml_syntax"
         class="diag-card {invalidYamlCount === 0 ? 'ok' : 'warn'}">
        <span class="diag-title">Invalid YAML syntax</span>
        <span class="diag-badge {invalidYamlCount === 0 ? 'badge-ok' : 'badge-warn'}">
          {invalidYamlCount === 0 ? 'All clear' : `${invalidYamlCount} ${invalidYamlCount === 1 ? 'file' : 'files'}`}
        </span>
      </a>
    </div>
  </section>
</Layout>

<style>
  .summary {
    color: #64748b;
    font-size: 0.9rem;
    margin: -0.75rem 0 2rem 0;
  }

  .group {
    margin-bottom: 2rem;
  }

  .group-label {
    font-size: 0.75rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: #94a3b8;
    margin: 0 0 0.75rem 0;
  }

  .type-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
    gap: 0.75rem;
  }

  .type-card {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    padding: 1rem 1.25rem;
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 0.75rem;
    text-decoration: none;
    transition: border-color 0.15s, box-shadow 0.15s, transform 0.15s;
  }

  .type-card:hover {
    border-color: #93c5fd;
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.1);
    transform: translateY(-2px);
  }

  .view-card {
    background: #f8fafc;
    border-style: dashed;
  }

  .view-card-warn {
    border-color: #fbbf24;
    background: #fffbeb;
  }

  .view-card-warn:hover {
    border-color: #f59e0b;
  }

  .type-name {
    font-weight: 600;
    font-size: 0.9rem;
    color: #1e293b;
  }

  .type-count {
    font-size: 0.8rem;
    color: #94a3b8;
    font-family: 'Fira Code', monospace;
  }

  /* Diagnostics */
  .diagnostics-section {
    margin-top: 1rem;
  }

  .diag-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
    gap: 0.75rem;
  }

  .diag-card {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding: 1rem 1.25rem;
    border-radius: 0.75rem;
    border: 1px solid #e2e8f0;
    text-decoration: none;
    background: white;
    transition: border-color 0.15s, box-shadow 0.15s, transform 0.15s;
  }

  .diag-card:hover {
    box-shadow: 0 4px 12px rgba(0,0,0,0.08);
    transform: translateY(-2px);
  }

  .diag-card.ok {
    border-color: #86efac;
    background: #f0fdf4;
  }

  .diag-card.ok:hover {
    border-color: #4ade80;
  }

  .diag-card.warn {
    border-color: #fca5a5;
  }

  .diag-card.warn:hover {
    border-color: #f87171;
  }

  .diag-title {
    font-weight: 600;
    font-size: 0.9rem;
    color: #1e293b;
  }

  .diag-badge {
    align-self: flex-start;
    font-size: 0.775rem;
    font-weight: 600;
    padding: 0.2rem 0.6rem;
    border-radius: 9999px;
  }

  .badge-ok {
    background: #dcfce7;
    color: #166534;
  }

  .badge-warn {
    background: #fee2e2;
    color: #991b1b;
  }
</style>
